/**
 * Card de configuração financeira do tenant — bloco Pro-rata.
 *
 * Permite ao administrador:
 *  - Ligar/desligar pro-rata.
 *  - Definir o modo de cobrança (charge_immediately, add_to_next_invoice,
 *    create_credit_note ou none).
 *  - Configurar se aplica na criação da assinatura e na troca de plano.
 *
 * RELEASE: 2026-06-17 v1.4.0 - Fase B (pro-rata mid-cycle): nova seção
 *          "Edição de recorrências ativas" com 3 controles novos
 *          (apply_on_subscription_edit, subscription_edit_mode,
 *          min_proration_amount). Sem alterar nenhum controle existente.
 * RELEASE: 2026-06-16 v1.3.0 - Refinos UX: ícones HelpCircle + Switches verdes.
 * RELEASE: 2026-06-16 v1.2.0 - FreeTierLimitBanner no topo do card.
 * RELEASE: 2026-06-16 v1.1.0 - Copy esclarecida para recorrências do tenant.
 * RELEASE: 2026-06-16 v1.0.0 - Versão inicial.
 */
import { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip';
import { InlineNotice } from '@/components/ui/inline-notice';
import { FreeTierLimitBanner } from '@/components/financial/FreeTierLimitBanner';
import { useFinancialSettings, type ProRataConfig, type ProRataMode, type SubscriptionEditMode } from '@/hooks/useFinancialSettings';
import { Input } from '@/components/ui/input';
import { Save, Calculator, HelpCircle, Receipt } from 'lucide-react';
import {
  SubscriptionJurosSection,
  JUROS_DEFAULT,
  type JurosValue,
} from '@/components/financial/subscription/SubscriptionJurosSection';


// Classes reutilizadas para padronizar o visual do Switch (verde quando ativo,
// igual ao usado na listagem /tenant/departments).
// Estilo padrão dos toggles agora vive em src/components/ui/switch.tsx
// (emerald-600 quando ligado + tamanho ~40% menor). Mantido aqui apenas o
// helper HelpTip abaixo.


// Componente auxiliar: ícone de ajuda com Tooltip — mesmo padrão usado em
// src/components/financial/EditSubscriptionModal.tsx.
function HelpTip({ text }: { text: React.ReactNode }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <HelpCircle className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
      </TooltipTrigger>
      <TooltipContent className="max-w-xs text-xs">{text}</TooltipContent>
    </Tooltip>
  );
}


// Opções de modo de aplicação de pro-rata exibidas no select
const MODE_OPTIONS: Array<{ value: ProRataMode; label: string; description: string }> = [
  {
    value: 'charge_immediately',
    label: 'Cobrar imediatamente',
    description: 'Gera uma cobrança proporcional na hora da contratação/upgrade.',
  },
  {
    value: 'add_to_next_invoice',
    label: 'Adicionar à próxima fatura',
    description: 'Inclui o valor proporcional na próxima fatura recorrente.',
  },
  {
    value: 'create_credit_note',
    label: 'Gerar crédito',
    description: 'Cria um crédito para o tenant aplicar em faturas futuras.',
  },
  {
    value: 'none',
    label: 'Não aplicar',
    description: 'Cobra valor cheio mesmo em períodos parciais.',
  },
];

// Opções do modo de ajuste mid-cycle (Fase B).
const EDIT_MODE_OPTIONS: Array<{ value: SubscriptionEditMode; label: string; description: string }> = [
  { value: 'inherit',             label: 'Usar o mesmo modo geral',  description: 'Aplica o "Modo de aplicação" acima também na edição mid-cycle.' },
  { value: 'charge_immediately',  label: 'Cobrar imediatamente',     description: 'Gera uma cobrança avulsa pro-rata na hora do upgrade.' },
  { value: 'add_to_next_invoice', label: 'Adicionar à próxima fatura', description: 'Soma o ajuste pro-rata na próxima fatura recorrente.' },
  { value: 'none',                label: 'Não aplicar',              description: 'Mudanças mid-cycle não geram ajuste — só valem no próximo ciclo.' },
];

const DEFAULT_CONFIG: ProRataConfig = {
  enabled: false,
  mode: 'charge_immediately',
  apply_on_create: true,
  apply_on_plan_change: true,
  apply_on_subscription_edit: true,
  subscription_edit_mode: 'inherit',
  min_proration_amount: 1.00,
  // v1.2.0 — Padrões de cobrança (UI prefill apenas)
  charge_juros_enabled: false,
  charge_juros: {},
  charge_expiracao_enabled: false,
  charge_dias_expiracao: 60,
  // v1.3.0 — Política de consumo automático de crédito do cliente
  auto_apply_customer_credit: true,
  customer_credit_prompt_mode: 'auto',
};


export function FinancialSettingsCard() {
  const { config, isLoading, isSaving, save } = useFinancialSettings();
  const [form, setForm] = useState<ProRataConfig>(DEFAULT_CONFIG);

  // Sincroniza estado local com config remota quando ela chega
  useEffect(() => {
    if (config) setForm({ ...DEFAULT_CONFIG, ...config });
  }, [config]);

  const handleSave = async () => {
    await save({
      enabled: form.enabled,
      mode: form.mode,
      apply_on_create: form.apply_on_create,
      apply_on_plan_change: form.apply_on_plan_change,
      apply_on_subscription_edit: form.apply_on_subscription_edit,
      subscription_edit_mode: form.subscription_edit_mode,
      min_proration_amount: Number(form.min_proration_amount) || 0,
      // v1.2.0 — Padrões de cobrança (UI prefill apenas)
      charge_juros_enabled: !!form.charge_juros_enabled,
      charge_juros: form.charge_juros ?? {},
      charge_expiracao_enabled: !!form.charge_expiracao_enabled,
      charge_dias_expiracao: Number(form.charge_dias_expiracao) || 60,
      // v1.3.0 — Política de consumo automático de crédito do cliente
      auto_apply_customer_credit: !!form.auto_apply_customer_credit,
      customer_credit_prompt_mode: form.customer_credit_prompt_mode ?? 'auto',
    });
  };


  if (isLoading) {
    return (
      <Card className="border-border/60 shadow-none p-6 space-y-4">
        <Skeleton className="h-5 w-48" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-10 w-full" />
      </Card>
    );
  }

  return (
    <TooltipProvider delayDuration={150}>
      <Card className="border-border/60 shadow-none">
        {/* Cabeçalho */}
        <div className="flex items-start gap-3 border-b border-border/50 px-6 py-4">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Calculator className="h-4 w-4" />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-semibold tracking-tight">Pro-rata de assinaturas</h3>
            <p className="text-xs text-muted-foreground">
              Define como o sistema cobra períodos parciais ao criar recorrências de
              cobrança com seus clientes ou ao trocar de plano de uma recorrência ativa.
            </p>
          </div>
        </div>

        <div className="space-y-5 p-6">
          {/*
            Banner do limite gratuito do módulo Financeiro.
            Renderiza apenas quando o tenant NÃO tem o add-on Módulo
            Financeiro contratado. Sem container externo para se ajustar
            ao padding do card. Mesma regra de 10 cobranças/mês das
            páginas /financial/*.
          */}
          <FreeTierLimitBanner entityLabel="cobranças" withContainer={false} />

          {/* Aviso de escopo — texto neutro, sem mencionar "workspace" para
              evitar confusão com o conceito de workspace do CRM. */}
          <InlineNotice variant="info">
            Estas configurações afetam as <strong>recorrências do módulo Financeiro</strong>,
            geradas em <strong>Financeiro → Recorrências</strong> para os seus clientes.
          </InlineNotice>

          {/*
            Linha principal — grid 2 colunas:
            ESQUERDA: Modo de aplicação (Select)
            DIREITA:  Habilitar cálculo de pro-rata (Switch)
            Em mobile, empilha verticalmente.
          */}
          <div className="grid gap-4 md:grid-cols-2">
            {/* Modo de aplicação */}
            <div className="space-y-2">
              <Label className="text-sm font-medium flex items-center gap-1.5">
                Modo de aplicação
                <HelpTip
                  text={
                    <>
                      Define quando o valor proporcional é cobrado:&nbsp;
                      <strong>Cobrar imediatamente</strong> gera uma cobrança avulsa na hora;{' '}
                      <strong>Adicionar à próxima fatura</strong> soma o valor proporcional na
                      próxima fatura recorrente; <strong>Gerar crédito</strong> registra um
                      crédito para abatimento futuro; <strong>Não aplicar</strong> cobra o valor
                      cheio mesmo em períodos parciais.
                    </>
                  }
                />
              </Label>
              <Select
                value={form.mode}
                disabled={!form.enabled}
                onValueChange={(v) => setForm((f) => ({ ...f, mode: v as ProRataMode }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o modo" />
                </SelectTrigger>
                <SelectContent>
                  {MODE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      <div className="flex flex-col">
                        <span className="text-sm">{opt.label}</span>
                        <span className="text-[11px] text-muted-foreground">{opt.description}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Habilitar cálculo de pro-rata */}
            <div className="flex items-start justify-between gap-4 rounded-md border border-border/60 px-3 py-2.5">
              <div className="min-w-0">
                <Label htmlFor="pro-rata-enabled" className="text-sm font-medium flex items-center gap-1.5">
                  Habilitar cálculo de pro-rata
                  <HelpTip
                    text="Quando ativo, recorrências com data de início no meio de um ciclo geram cobrança proporcional aos dias restantes. Desligado, sempre cobra o valor cheio do ciclo."
                  />
                </Label>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  Quando desligado, todas as suas recorrências cobram o valor cheio do ciclo.
                </p>
              </div>
              <Switch
                id="pro-rata-enabled"
                checked={form.enabled}
                onCheckedChange={(v) => setForm((f) => ({ ...f, enabled: v }))}
               
              />
            </div>
          </div>

          {/* Quando aplicar */}
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="flex items-center justify-between rounded-md border border-border/60 px-3 py-2.5">
              <div>
                <Label htmlFor="apply-create" className="text-sm flex items-center gap-1.5">
                  Aplicar na criação
                  <HelpTip text="Ativa o pro-rata no primeiro ciclo quando uma nova recorrência começa fora do dia padrão do ciclo." />
                </Label>
                <p className="text-[11px] text-muted-foreground">
                  Aplicado no primeiro ciclo de novas recorrências.
                </p>
              </div>
              <Switch
                id="apply-create"
                checked={form.apply_on_create}
                disabled={!form.enabled}
                onCheckedChange={(v) => setForm((f) => ({ ...f, apply_on_create: v }))}
               
              />
            </div>
            <div className="flex items-center justify-between rounded-md border border-border/60 px-3 py-2.5">
              <div>
                <Label htmlFor="apply-change" className="text-sm flex items-center gap-1.5">
                  Aplicar em trocas de plano
                  <HelpTip text="Ativa o pro-rata quando o valor de uma recorrência ativa é alterado no meio do ciclo (upgrade/downgrade), cobrando ou creditando a diferença proporcional." />
                </Label>
                <p className="text-[11px] text-muted-foreground">
                  Aplicado ao alterar o valor de uma recorrência no meio do ciclo.
                </p>
              </div>
              <Switch
                id="apply-change"
                checked={form.apply_on_plan_change}
                disabled={!form.enabled}
                onCheckedChange={(v) => setForm((f) => ({ ...f, apply_on_plan_change: v }))}
               
              />
            </div>
          </div>

          {/* ================================================================
              Fase B — Edição de recorrências ativas (pro-rata mid-cycle)
              Aparece apenas quando o pro-rata global está habilitado e
              "Aplicar em trocas de plano" também está ativo.
             ================================================================ */}
          <div className="rounded-lg border border-border/60 bg-muted/30 p-4 space-y-4">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <Label htmlFor="apply-edit" className="text-sm font-semibold flex items-center gap-1.5">
                  Aplicar ao editar recorrência ativa (mid-cycle)
                  <HelpTip
                    text={
                      <>
                        Quando uma recorrência <strong>já paga</strong> tem seus itens/valor alterados no
                        meio do ciclo (upgrade ou downgrade), o sistema gera <strong>automaticamente</strong>{' '}
                        um ajuste pro-rata dos dias restantes — cobrança extra (upgrade) ou crédito em
                        conta (downgrade). Comportamento similar ao do Stripe.
                      </>
                    }
                  />
                </Label>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  Recomendado <strong>ligado</strong>. Desligado, mudanças no meio do ciclo só valem no próximo vencimento (risco de prejuízo no upgrade).
                </p>
              </div>
              <Switch
                id="apply-edit"
                checked={form.apply_on_subscription_edit}
                disabled={!form.enabled || !form.apply_on_plan_change}
                onCheckedChange={(v) => setForm((f) => ({ ...f, apply_on_subscription_edit: v }))}
               
              />
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              {/* Modo do ajuste mid-cycle */}
              <div className="space-y-2">
                <Label className="text-sm font-medium flex items-center gap-1.5">
                  Modo do ajuste mid-cycle
                  <HelpTip text="Define como o ajuste proporcional é entregue ao cliente quando a recorrência é editada no meio de um ciclo já pago." />
                </Label>
                <Select
                  value={form.subscription_edit_mode}
                  disabled={!form.enabled || !form.apply_on_plan_change || !form.apply_on_subscription_edit}
                  onValueChange={(v) => setForm((f) => ({ ...f, subscription_edit_mode: v as SubscriptionEditMode }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o modo" />
                  </SelectTrigger>
                  <SelectContent>
                    {EDIT_MODE_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        <div className="flex flex-col">
                          <span className="text-sm">{opt.label}</span>
                          <span className="text-[11px] text-muted-foreground">{opt.description}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Valor mínimo do ajuste */}
              <div className="space-y-2">
                <Label htmlFor="min-amt" className="text-sm font-medium flex items-center gap-1.5">
                  Valor mínimo do ajuste (R$)
                  <HelpTip text="Ajustes pro-rata com valor absoluto menor que este limite são ignorados — evita gerar cobranças/créditos de centavos. Padrão: R$ 1,00." />
                </Label>
                <Input
                  id="min-amt"
                  type="number"
                  inputMode="decimal"
                  min={0}
                  max={1000}
                  step="0.01"
                  value={form.min_proration_amount ?? 0}
                  disabled={!form.enabled || !form.apply_on_plan_change || !form.apply_on_subscription_edit}
                  onChange={(e) => setForm((f) => ({ ...f, min_proration_amount: Number(e.target.value) }))}
                />
                <p className="text-[11px] text-muted-foreground">
                  Diferenças abaixo deste valor não geram cobrança nem crédito.
                </p>
              </div>
            </div>

            {/* ============================================================
                v1.3.0 — Política de consumo automático de crédito do cliente
                Quando habilitado, o backend (update_financeiro_assinatura
                v1.11.0) consome FIFO o saldo de crédito do cliente antes
                de criar a cobrança pro-rata mid-cycle. Se o crédito cobrir
                tudo, nenhuma cobrança é gerada. Política enforced 100% no
                servidor — flag do front é ignorada quando incompatível.
               ============================================================ */}
            <div className="rounded-md border border-border/60 bg-background/60 p-3 space-y-3">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <Label htmlFor="auto-apply-credit" className="text-sm font-medium flex items-center gap-1.5">
                    Aproveitar crédito do cliente automaticamente
                    <HelpTip
                      text={
                        <>
                          Quando o cliente tem <strong>saldo de crédito</strong> disponível, o
                          sistema abate esse valor da nova cobrança pro-rata antes de gerá-la.
                          Se o saldo cobrir o ajuste inteiro, nenhuma cobrança é criada.
                        </>
                      }
                    />
                  </Label>
                  <p className="text-[11px] text-muted-foreground mt-0.5">
                    Evita gerar cobrança quando o cliente já tem crédito suficiente.
                  </p>
                </div>
                <Switch
                  id="auto-apply-credit"
                  checked={!!form.auto_apply_customer_credit}
                  disabled={!form.enabled || !form.apply_on_plan_change || !form.apply_on_subscription_edit}
                  onCheckedChange={(v) =>
                    setForm((f) => ({ ...f, auto_apply_customer_credit: v }))
                  }
                />
              </div>

              {/* Sub-opção: como aplicar (automático vs perguntar) */}
              {form.auto_apply_customer_credit && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium flex items-center gap-1.5">
                    Como aplicar o crédito
                    <HelpTip
                      text={
                        <>
                          <strong>Aplicar automaticamente</strong>: o sistema abate o crédito
                          sem perguntar.{' '}
                          <strong>Perguntar ao salvar</strong>: o usuário precisa confirmar
                          marcando uma caixa de seleção no modal de edição da recorrência.
                        </>
                      }
                    />
                  </Label>
                  <Select
                    value={form.customer_credit_prompt_mode ?? 'auto'}
                    disabled={!form.enabled || !form.apply_on_plan_change || !form.apply_on_subscription_edit}
                    onValueChange={(v) =>
                      setForm((f) => ({
                        ...f,
                        customer_credit_prompt_mode: v as 'auto' | 'ask',
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto">
                        <div className="flex flex-col">
                          <span className="text-sm">Aplicar automaticamente</span>
                          <span className="text-[11px] text-muted-foreground">
                            Consome o crédito sem perguntar ao usuário.
                          </span>
                        </div>
                      </SelectItem>
                      <SelectItem value="ask">
                        <div className="flex flex-col">
                          <span className="text-sm">Perguntar ao salvar</span>
                          <span className="text-[11px] text-muted-foreground">
                            Exige confirmação explícita no modal de edição.
                          </span>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </div>




          {/* ================================================================
              v1.2.0 — Padrões de cobrança (UI prefill apenas)
              Quando ligados, pré-preenchem os modais de criação de cobrança
              (avulsa, parcelada ou recorrência). O usuário pode desmarcar
              pontualmente naquela cobrança. Nenhum default é aplicado pelo
              backend — eles existem apenas para conveniência na tela.
             ================================================================ */}
          <div className="rounded-lg border border-border/60 p-4 space-y-4">
            <div className="flex items-start gap-3 border-b border-border/50 pb-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <Receipt className="h-4 w-4" />
              </div>
              <div className="flex-1">
                <h4 className="text-sm font-semibold tracking-tight">Padrões de cobrança</h4>
                <p className="text-xs text-muted-foreground">
                  Pré-preenchem os campos ao abrir uma nova cobrança (avulsa, parcelada ou recorrência).
                  Você pode desmarcar/alterar pontualmente em cada cobrança.
                </p>
              </div>
            </div>

            {/* Juros e multa padrão — usa o próprio toggle nativo do
                SubscriptionJurosSection como única fonte de verdade
                (evita 2 toggles redundantes confundindo o cliente). */}
            <div className="pl-1">
              <SubscriptionJurosSection
                value={{
                  enabled: !!form.charge_juros_enabled,
                  multa_tipo: (form.charge_juros?.multa_tipo as 'percent' | 'fixed') || 'percent',
                  multa_valor: Number(form.charge_juros?.multa_valor) || 0,
                  juros_tipo: (form.charge_juros?.juros_tipo as 'percent' | 'fixed') || 'percent',
                  juros_valor: Number(form.charge_juros?.juros_valor) || 0,
                } as JurosValue}
                onChange={(v) => setForm((f) => ({
                  ...f,
                  charge_juros_enabled: !!v.enabled,
                  charge_juros: {
                    multa_tipo: v.multa_tipo,
                    multa_valor: v.multa_valor,
                    juros_tipo: v.juros_tipo,
                    juros_valor: v.juros_valor,
                  },
                }))}
                defaultOpen
              />
            </div>


            {/* Prazo de expiração padrão */}
            <div className="flex items-start justify-between gap-4 rounded-md border border-border/60 px-3 py-2.5">
              <div className="min-w-0">
                <Label htmlFor="charge-exp-enabled" className="text-sm font-medium flex items-center gap-1.5">
                  Definir prazo de expiração padrão
                  <HelpTip text="Quando ligado, toda nova cobrança usa o prazo de expiração (em dias após o vencimento) definido abaixo. Você pode alterar pontualmente em cada cobrança." />
                </Label>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  Dias após o vencimento em que o boleto/PIX deixa de ser pagável.
                </p>
              </div>
              <Switch
                id="charge-exp-enabled"
                checked={!!form.charge_expiracao_enabled}
                onCheckedChange={(v) => setForm((f) => ({ ...f, charge_expiracao_enabled: v }))}
               
              />
            </div>

            {form.charge_expiracao_enabled && (
              <div className="grid gap-2 md:max-w-xs">
                <Label htmlFor="charge-dias-exp" className="text-sm font-medium">
                  Dias de expiração (após vencimento)
                </Label>
                <Input
                  id="charge-dias-exp"
                  type="number"
                  inputMode="numeric"
                  min={0}
                  max={365}
                  step={1}
                  value={form.charge_dias_expiracao ?? 60}
                  onChange={(e) => {
                    const n = Number(e.target.value);
                    setForm((f) => ({
                      ...f,
                      charge_dias_expiracao: Number.isFinite(n) ? Math.max(0, Math.min(365, n)) : 60,
                    }));
                  }}
                />
                <p className="text-[11px] text-muted-foreground">
                  Valor entre 0 e 365 dias. Sugestão: 60.
                </p>
              </div>
            )}
          </div>

          {/* v2026-06-27 — Bloco "Recebimento de parcelamentos por cartão
              (link público)" foi MOVIDO para a tela de conta corrente, pois
              a regra é por provider de pagamento, não por tenant. Mantido
              como nota histórica para evitar reintrodução acidental. */}




          {!form.enabled && (

            <InlineNotice variant="warning">
              Pro-rata está desligado. Novas recorrências e mudanças de plano cobrarão sempre o valor cheio do ciclo.
            </InlineNotice>
          )}



          {/* Rodapé com ação salvar */}
          <div className="flex justify-end pt-2">
            <Button onClick={handleSave} disabled={isSaving} className="gap-2">
              <Save className="h-4 w-4" />
              {isSaving ? 'Salvando...' : 'Salvar alterações'}
            </Button>
          </div>
        </div>
      </Card>
    </TooltipProvider>
  );
}

export default FinancialSettingsCard;

